import type { AlmightyReferenceItem } from './almightyReference'
import { isHailuo03Model } from './videoModelCapabilities'

type HistoryParams = Record<string, any>

const ALMIGHTY_CREATE_TYPES = new Set([
    'almightyreferencetovideo',
    'almightyreferencetovideobatch',
])

const normalizeCreateType = (value: unknown) =>
    typeof value === 'string'
        ? value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
        : ''

const hasItems = (value: unknown) => Array.isArray(value) && value.length > 0

export const getAlmightyHistoryParams = (item: any): HistoryParams => {
    const param = item?.param && typeof item.param === 'object'
        ? item.param
        : {}
    const requestParams = item?.request_params && typeof item.request_params === 'object'
        ? item.request_params
        : {}
    return { ...param, ...requestParams }
}

export const isAlmightyReferenceHistoryCreateType = (item: any) => {
    const params = getAlmightyHistoryParams(item)
    return [item?.create_type, params?.create_type].some(value =>
        ALMIGHTY_CREATE_TYPES.has(normalizeCreateType(value))
    )
}

export const isAlmightyReferenceHistoryRecord = (item: any) => {
    const params = getAlmightyHistoryParams(item)
    return Boolean(
        isAlmightyReferenceHistoryCreateType(item) ||
        params?.mode === 'ALMIGHTY_REFERENCE' ||
        params?.almighty_mode === true ||
        hasItems(params?.almighty_reference_items) ||
        hasItems(params?.almighty_images) ||
        hasItems(params?.almighty_videos) ||
        hasItems(params?.almighty_audios)
    )
}

export const isHailuo03AlmightyHistoryRecord = (item: any) => {
    if (!isAlmightyReferenceHistoryRecord(item)) return false
    const params = getAlmightyHistoryParams(item)
    return [
        item?.model,
        item?.model_key,
        params?.model,
        params?.model_key,
        params?.video_model,
    ].some(modelKey => isHailuo03Model(modelKey))
}

const getMediaUrl = (value: any) => {
    if (typeof value === 'string') return value
    for (const key of ['url', 'src', 'file_url', 'image_url', 'video_url', 'audio_url']) {
        if (typeof value?.[key] === 'string' && value[key]) return value[key]
    }
    return ''
}

const normalizeItems = (
    values: unknown,
    kind: AlmightyReferenceItem['kind'],
    idPrefix: string
): AlmightyReferenceItem[] => {
    if (!Array.isArray(values)) return []
    return values
        .map((value: any, index): AlmightyReferenceItem | null => {
            const url = getMediaUrl(value)
            if (!url) return null
            const duration = Number(value?.duration || 0)
            return {
                ...(value && typeof value === 'object' ? value : {}),
                id: typeof value?.id === 'string' && value.id
                    ? value.id
                    : `${idPrefix}-${kind}-${index}`,
                kind,
                url,
                name:
                    value?.name ||
                    value?.filename ||
                    value?.title ||
                    undefined,
                duration: Number.isFinite(duration) && duration > 0
                    ? duration
                    : undefined,
            }
        })
        .filter((item): item is AlmightyReferenceItem => !!item)
}

const normalizeFirstPopulatedItems = (
    candidates: unknown[],
    kind: AlmightyReferenceItem['kind'],
    idPrefix: string
) => {
    for (const candidate of candidates) {
        const items = normalizeItems(candidate, kind, idPrefix)
        if (items.length > 0) return items
    }
    return []
}

export const getAlmightyHistoryReferenceItems = (item: any): AlmightyReferenceItem[] => {
    if (!isAlmightyReferenceHistoryRecord(item)) return []
    const params = getAlmightyHistoryParams(item)

    if (Array.isArray(params?.almighty_reference_items)) {
        const directItems = params.almighty_reference_items
            .map((value: any, index): AlmightyReferenceItem | null => {
                if (!['image', 'video', 'audio'].includes(value?.kind)) return null
                const url = getMediaUrl(value)
                if (!url) return null
                return {
                    ...value,
                    id: typeof value.id === 'string' && value.id
                        ? value.id
                        : `almighty-reference-${value.kind}-${index}`,
                    url,
                }
            })
            .filter((value): value is AlmightyReferenceItem => !!value)
        if (directItems.length > 0) return directItems
    }

    const namedItems = [
        ...normalizeItems(params?.almighty_images, 'image', 'almighty'),
        ...normalizeItems(params?.almighty_videos, 'video', 'almighty'),
        ...normalizeItems(params?.almighty_audios, 'audio', 'almighty'),
    ]
    if (namedItems.length > 0) return namedItems

    return [
        ...normalizeFirstPopulatedItems(
            [params?.pic_urls, params?.images],
            'image',
            'almighty-flat'
        ),
        ...normalizeFirstPopulatedItems(
            [params?.video_urls, params?.videos],
            'video',
            'almighty-flat'
        ),
        ...normalizeFirstPopulatedItems(
            [params?.audio_urls, params?.audios],
            'audio',
            'almighty-flat'
        ),
    ]
}
